import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import { Edit, Delete, Add, Visibility, VisibilityOff } from '@mui/icons-material';
import { API_BASE_URL } from '../utils/api';

interface BannerMessage {
  id: number;
  message: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

const AdminPanel: React.FC = () => {
  const [messages, setMessages] = useState<BannerMessage[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMessage, setEditingMessage] = useState<BannerMessage | null>(null);
  const [formData, setFormData] = useState({
    message: '',
    order: 0,
    isActive: true
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const API_BASE = API_BASE_URL;

  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/banner-messages`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      showSnackbar('Failed to fetch messages', 'error');
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleSubmit = async () => {
    try {
      const url = editingMessage 
        ? `${API_BASE}/admin/banner-messages/${editingMessage.id}`
        : `${API_BASE}/admin/banner-messages`;
      
      const method = editingMessage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        showSnackbar(
          editingMessage ? 'Message updated successfully!' : 'Message created successfully!',
          'success'
        );
        handleCloseDialog();
        fetchMessages();
      } else {
        throw new Error('Failed to save message');
      }
    } catch (error) {
      console.error('Error saving message:', error);
      showSnackbar('Failed to save message', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      const response = await fetch(`${API_BASE}/admin/banner-messages/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showSnackbar('Message deleted successfully!', 'success');
        fetchMessages();
      } else {
        throw new Error('Failed to delete message');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      showSnackbar('Failed to delete message', 'error');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/admin/banner-messages/${id}/toggle`, {
        method: 'PATCH',
      });

      if (response.ok) {
        showSnackbar('Message status updated!', 'success');
        fetchMessages();
      } else {
        throw new Error('Failed to toggle status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleEdit = (message: BannerMessage) => {
    setEditingMessage(message);
    setFormData({
      message: message.message,
      order: message.order,
      isActive: message.isActive
    });
    setOpenDialog(true);
  };

  const handleAdd = () => {
    setEditingMessage(null);
    setFormData({
      message: '',
      order: 0,
      isActive: true
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMessage(null);
    setFormData({
      message: '',
      order: 0,
      isActive: true
    });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Panel - Banner Messages
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage banner messages that appear on the dashboard
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAdd}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          Add New Message
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((message) => (
                <TableRow key={message.id}>
                  <TableCell>{message.order}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography noWrap>
                      {message.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {message.isActive ? (
                        <Visibility color="success" />
                      ) : (
                        <VisibilityOff color="disabled" />
                      )}
                      <Switch
                        checked={message.isActive}
                        onChange={() => handleToggleStatus(message.id)}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    {new Date(message.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEdit(message)}
                      color="primary"
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(message.id)}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMessage ? 'Edit Banner Message' : 'Add New Banner Message'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.message.trim()}
          >
            {editingMessage ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPanel; 